import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Inhibition — FTUE demo (3 essais).
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
const DEMO_STEPS = [
    {
        idx: 1,
        titleKey: 'inhibition.demo.step1_title',
        hintKey: 'inhibition.demo.step1_hint',
        goDirection: 'right',
        isStop: false,
        ssdMs: 0,
    },
    {
        idx: 2,
        titleKey: 'inhibition.demo.step2_title',
        hintKey: 'inhibition.demo.step2_hint',
        goDirection: 'left',
        isStop: true,
        ssdMs: 400,
    },
    {
        idx: 3,
        titleKey: 'inhibition.demo.step3_title',
        hintKey: 'inhibition.demo.step3_hint',
        goDirection: 'right',
        isStop: true,
        ssdMs: 200,
    },
];
export function Demo({ level: _level, onComplete, onSkip }) {
    const { t } = useTranslation(['games', 'common']);
    const [stepIdx, setStepIdx] = useState(0);
    const [phase, setPhase] = useState('fixation');
    const [stopActive, setStopActive] = useState(false);
    const [picked, setPicked] = useState(null);
    const fixationTimer = useRef(null);
    const stopTimer = useRef(null);
    const windowTimer = useRef(null);
    const step = DEMO_STEPS[stepIdx];
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    useEffect(() => {
        setPhase('fixation');
        setStopActive(false);
        setPicked(null);
        fixationTimer.current = setTimeout(() => {
            setPhase('go');
            setPhase('awaiting');
            if (step.isStop) {
                stopTimer.current = setTimeout(() => setStopActive(true), step.ssdMs);
            }
            windowTimer.current = setTimeout(() => {
                setPhase('feedback');
            }, 1500);
        }, 600);
        return () => {
            if (fixationTimer.current)
                clearTimeout(fixationTimer.current);
            if (stopTimer.current)
                clearTimeout(stopTimer.current);
            if (windowTimer.current)
                clearTimeout(windowTimer.current);
        };
    }, [stepIdx, step.isStop, step.ssdMs]);
    const correct = (() => {
        if (phase !== 'feedback')
            return null;
        if (step.isStop)
            return picked === null;
        return picked === step.goDirection;
    })();
    const handlePick = (d) => {
        if (phase !== 'awaiting')
            return;
        if (windowTimer.current)
            clearTimeout(windowTimer.current);
        setPicked(d);
        setPhase('feedback');
    };
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setStepIdx((i) => i + 1);
    };
    const showArrow = phase === 'awaiting';
    const feedbackLabel = (() => {
        if (correct)
            return '✓';
        return step.isStop
            ? `✗ ${t('inhibition.feedback.should_have_stopped')}`
            : `✗ ${t('inhibition.feedback.tap_missed')}`;
    })();
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: t('common:demo.eyebrow_with_master', { master: t('inhibition.demo.master') }) }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: t(step.titleKey) }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: t(step.hintKey) }), _jsxs("div", { className: "relative flex h-[200px] w-full max-w-[280px] items-center justify-center", style: { background: '#1a1a1a' }, children: [phase === 'fixation' && (_jsx("span", { className: "font-display text-[36px] text-cyan-surface", children: "+" })), showArrow && (_jsx("span", { className: [
                            'font-display text-[72px]',
                            stopActive ? 'text-cyan-media' : 'text-cyan-surface',
                        ].join(' '), children: step.goDirection === 'left' ? '←' : '→' })), stopActive && phase === 'awaiting' && (_jsx("div", { className: "absolute inset-0 flex items-center justify-center", children: _jsx("div", { className: "h-[160px] w-[160px] rounded-full border-[6px] border-cyan-media opacity-60" }) })), phase === 'feedback' && (_jsx("span", { className: [
                            'font-display text-[20px]',
                            correct ? 'text-cyan-core' : 'text-cyan-media',
                        ].join(' '), children: feedbackLabel }))] }), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [phase === 'awaiting' && (_jsxs("div", { className: "flex justify-center gap-s3", children: [_jsx("button", { type: "button", onClick: () => handlePick('left'), "aria-label": t('inhibition.tap_left_aria'), className: "flex h-[60px] w-[60px] items-center justify-center rounded-full bg-cyan-surface text-[28px] text-cyan-text border border-cyan-border hover:scale-110 hover:bg-cyan-network/15 active:scale-95", children: "\u2190" }), _jsx("button", { type: "button", onClick: () => handlePick('right'), "aria-label": t('inhibition.tap_right_aria'), className: "flex h-[60px] w-[60px] items-center justify-center rounded-full bg-cyan-surface text-[28px] text-cyan-text border border-cyan-border hover:scale-110 hover:bg-cyan-network/15 active:scale-95", children: "\u2192" })] })), phase === 'feedback' && (_jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? t('common:actions.start_session') : t('common:actions.next_trial') })), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.skip_demo') })] })] }));
}
//# sourceMappingURL=demo.js.map